import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const milestones = [
  { year: '1995', titleKey: 'milestones.m1.title', descKey: 'milestones.m1.desc' },
  { year: '2003', titleKey: 'milestones.m2.title', descKey: 'milestones.m2.desc' },
  { year: '2006', titleKey: 'milestones.m3.title', descKey: 'milestones.m3.desc' },
  { year: '2015', titleKey: 'milestones.m4.title', descKey: 'milestones.m4.desc' },
  { year: '2022', titleKey: 'milestones.m5.title', descKey: 'milestones.m5.desc' },
  { year: '2026', titleKey: 'milestones.m6.title', descKey: 'milestones.m6.desc' },
];

export default function MilestonesSection() {
  const { t } = useTranslation('about');
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="w-full bg-cream-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-xs font-semibold text-warm-500 uppercase tracking-[0.2em] mb-3 block">
              {t('milestones.label')}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-700 mb-4">
              {t('milestones.title')}
            </h2>
            <p className="text-sm md:text-base text-ink-400 max-w-2xl mx-auto leading-relaxed">
              {t('milestones.subtitle')}
            </p>
            <div className="w-16 h-1 bg-warm-500 mx-auto mt-6 rounded-full" />
          </div>

          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-ink-200" />

            <div className="space-y-8">
              {milestones.map((item, index) => {
                const isLeft = index % 2 === 0;
                return (
                  <div
                    key={item.year}
                    className={`relative flex flex-col md:flex-row md:items-center gap-4 md:gap-8 transition-all duration-700 ${
                      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div
                      className={`absolute left-4 md:left-1/2 top-6 -translate-x-1/2 w-3 h-3 rounded-full bg-warm-500 border-2 border-white z-10`}
                    />
                    <div
                      className={`md:w-1/2 pl-10 md:pl-0 ${
                        isLeft ? 'md:pr-8 md:text-right' : 'md:order-2 md:pl-8'
                      }`}
                    >
                      <div className="text-sm font-bold text-warm-500 mb-1">{item.year}</div>
                      <h3 className="text-base md:text-lg font-semibold text-ink-700 mb-1">
                        {t(item.titleKey)}
                      </h3>
                      <p className="text-xs md:text-sm text-ink-400 leading-relaxed">
                        {t(item.descKey)}
                      </p>
                    </div>
                    <div className={`md:w-1/2 ${isLeft ? 'md:order-2 md:pl-8' : 'md:pr-8'}`} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}